import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Ban, LockOpen, ShieldOff, Timer, X } from 'lucide-react';
import {
  entsperreKonto,
  fetchSperren,
  hebeAlleSperrenAuf,
  hebeSperreAuf,
  sperreKonto,
  type Anmeldesperre,
} from '@/api/sicherheit.api';
import { getUsers, type User } from '@/api/users.api';
import { useConfirm } from '@/components/common/ConfirmDialog';
import { zahl, zeitpunkt } from '@/components/format';

// ─── Sperren ────────────────────────────────────────────────────────────────
//
// Zwei Dinge, die leicht verwechselt werden und deshalb hier getrennt stehen:
//
//   ANMELDESPERRE  gilt für eine Adresse, entsteht von selbst durch zu viele
//                  Versuche und endet von selbst. Trifft alle hinter demselben
//                  Anschluss.
//   KONTOSPERRE    gilt für eine Person, wird von Hand gesetzt und bleibt, bis
//                  jemand sie aufhebt. Mit einem Text, den der Betroffene beim
//                  Anmelden zu sehen bekommt.
//
// Wer eine Kontosperre setzt, wo eine Anmeldesperre gemeint war, sperrt einen
// Kunden dauerhaft aus, weil sein Nachbar sein Passwort vergessen hat.

const ART_LABEL: Record<string, string> = {
  login: 'Anmeldung',
  webauthn: 'Sicherheitsschlüssel',
  refresh: 'Sitzungserneuerung',
};

function verbleibend(iso: string): string {
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return 'abgelaufen';
  const min = Math.ceil(ms / 60000);
  return min < 60 ? `noch ${min} min` : `noch ${Math.round(min / 60)} h`;
}

// ─── Anmeldesperren ─────────────────────────────────────────────────────────

function Anmeldesperren() {
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'lockouts'],
    queryFn: fetchSperren,
    // Zähler ändern sich laufend; eine halbe Minute ist frisch genug, ohne
    // die Seite zu einem Live-Ticker zu machen.
    refetchInterval: 30_000,
  });

  const aktualisieren = () => {
    queryClient.invalidateQueries({ queryKey: ['admin', 'lockouts'] });
    queryClient.invalidateQueries({ queryKey: ['admin', 'security'] });
  };

  const einzeln = useMutation({
    mutationFn: hebeSperreAuf,
    onSuccess: () => {
      aktualisieren();
      toast.success('Sperre aufgehoben.');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const alle = useMutation({
    mutationFn: hebeAlleSperrenAuf,
    onSuccess: (r) => {
      aktualisieren();
      toast.success(`${r.anzahl} Zähler zurückgesetzt.`);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const alleAufheben = async () => {
    const ok = await confirm({
      title: 'Alle Sperren aufheben?',
      message:
        'Setzt jeden laufenden Zähler zurück – auch die, die gerade jemanden ' +
        'vom Durchprobieren abhalten. Sinnvoll nach einer Fehlkonfiguration, ' +
        'nicht als Routine.',
      confirmLabel: 'Alle aufheben',
      variant: 'danger',
    });
    if (ok) alle.mutate();
  };

  const zeilen: Anmeldesperre[] = data?.data ?? [];
  const gesperrte = zeilen.filter((z) => z.gesperrt);

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-900">
            <Timer className="h-4 w-4 text-slate-400" aria-hidden="true" />
            Anmeldesperren
          </h2>
          <p className="mt-1 text-sm leading-relaxed text-slate-600">
            Gilt je Adresse und läuft von selbst ab. Gezählt wird nach
            Fehlversuchen – eine geglückte Anmeldung zählt nicht mit. Aufgeführt
            sind auch Zähler, die die Grenze noch nicht erreicht haben.
          </p>
        </div>
        {zeilen.length > 0 && (
          <button
            onClick={alleAufheben}
            disabled={alle.isPending}
            className="shrink-0 rounded-md border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            Alle aufheben
          </button>
        )}
      </div>

      {isLoading && <p className="mt-4 text-sm text-slate-400">Wird geladen…</p>}

      {!isLoading && zeilen.length === 0 && (
        <p className="mt-4 text-sm text-slate-400">
          Kein laufender Zähler. Niemand ist ausgesperrt.
        </p>
      )}

      {zeilen.length > 0 && (
        <>
          <p className="mt-3 text-xs text-slate-400">
            {gesperrte.length === 0
              ? `${zahl(zeilen.length)} laufende Zähler, keiner davon sperrt.`
              : `${zahl(gesperrte.length)} von ${zahl(zeilen.length)} Zählern sperren gerade.`}
          </p>
          <ul className="mt-3 divide-y divide-slate-100">
            {zeilen.map((z) => (
              <li key={z.key} className="flex flex-wrap items-center gap-3 py-2.5">
                <span
                  className={`inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                    z.gesperrt ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-600'
                  }`}
                >
                  {z.gesperrt ? 'gesperrt' : 'zählt'}
                </span>
                <span className="min-w-0 flex-1 truncate font-mono text-xs text-slate-700">
                  {z.adresse}
                </span>
                <span className="shrink-0 text-xs text-slate-500">
                  {ART_LABEL[z.art] ?? z.art}
                  {z.port && ` (${z.port === '9174' || z.port === '4001' ? 'Konsole' : 'Anwendung'})`}{' '}
                  · {zahl(z.versuche)}
                  {data?.grenzen[z.art] ? ` / ${zahl(data.grenzen[z.art])}` : ''} ·{' '}
                  {verbleibend(z.laeuftAbAm)}
                </span>
                <button
                  onClick={() => einzeln.mutate(z.key)}
                  disabled={einzeln.isPending}
                  className="shrink-0 rounded-md border border-slate-300 px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                >
                  Aufheben
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

// ─── Kontosperren ───────────────────────────────────────────────────────────

function Kontosperren() {
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const [sperren, setSperren] = useState<User | null>(null);

  const { data: konten, isLoading } = useQuery({ queryKey: ['users'], queryFn: getUsers });

  const aktualisieren = () => queryClient.invalidateQueries({ queryKey: ['users'] });

  const entsperren = useMutation({
    mutationFn: entsperreKonto,
    onSuccess: () => {
      aktualisieren();
      toast.success('Konto entsperrt.');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const gesperrt = (konten ?? []).filter((k) => !k.isActive);
  const aktiv = (konten ?? []).filter((k) => k.isActive);

  const jetztEntsperren = async (k: User) => {
    const ok = await confirm({
      title: `${k.email} entsperren?`,
      message: 'Das Konto kann sich danach wieder anmelden.',
      confirmLabel: 'Entsperren',
    });
    if (ok) entsperren.mutate(k.id);
  };

  return (
    <>
      <section className="rounded-xl border border-slate-200 bg-white p-5">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-900">
          <Ban className="h-4 w-4 text-slate-400" aria-hidden="true" />
          Gesperrte Konten
        </h2>
        <p className="mt-1 text-sm leading-relaxed text-slate-600">
          Gilt für eine Person und bleibt, bis sie jemand aufhebt. Die Begründung
          bekommt der Betroffene beim Anmeldeversuch zu sehen.
        </p>

        {isLoading && <p className="mt-4 text-sm text-slate-400">Wird geladen…</p>}

        {!isLoading && gesperrt.length === 0 && (
          <p className="mt-4 text-sm text-slate-400">Kein Konto gesperrt.</p>
        )}

        {gesperrt.length > 0 && (
          <ul className="mt-4 divide-y divide-slate-100">
            {gesperrt.map((k) => (
              <li key={k.id} className="flex flex-wrap items-start gap-3 py-3">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-slate-900">{k.email}</p>
                  <p className="mt-0.5 text-sm text-slate-600">
                    {k.deactivatedReason ?? (
                      <span className="italic text-slate-400">Ohne Begründung gesperrt</span>
                    )}
                  </p>
                  {k.deactivatedAt && (
                    <p className="mt-0.5 text-xs text-slate-400">
                      seit {zeitpunkt(k.deactivatedAt)}
                    </p>
                  )}
                </div>
                <button
                  onClick={() => jetztEntsperren(k)}
                  disabled={entsperren.isPending}
                  className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-slate-300 px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                >
                  <LockOpen className="h-3.5 w-3.5" aria-hidden="true" />
                  Entsperren
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="rounded-xl border border-slate-200 bg-white p-5">
        <h2 className="text-sm font-semibold text-slate-900">Konto sperren</h2>
        <p className="mt-1 text-sm leading-relaxed text-slate-600">
          Beendet laufende Sitzungen sofort. Ein gesperrtes Konto, das bis zum
          Ablauf seines Tokens weiterarbeitet, wäre nicht gesperrt.
        </p>

        <div className="mt-4 max-h-72 divide-y divide-slate-100 overflow-y-auto rounded-lg border border-slate-200">
          {aktiv.map((k) => (
            <div key={k.id} className="flex items-center gap-3 px-3 py-2">
              <span className="min-w-0 flex-1 truncate text-sm text-slate-700">{k.email}</span>
              {k.role === 'admin' && (
                <span className="shrink-0 rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-700">
                  Betreiber
                </span>
              )}
              <button
                onClick={() => setSperren(k)}
                className="shrink-0 rounded-md border border-red-200 px-2.5 py-1 text-xs font-medium text-red-600 hover:bg-red-50"
              >
                Sperren
              </button>
            </div>
          ))}
          {aktiv.length === 0 && !isLoading && (
            <p className="px-3 py-4 text-sm text-slate-400">Kein aktives Konto.</p>
          )}
        </div>
      </section>

      {sperren && (
        <SperrDialog
          konto={sperren}
          onClose={() => setSperren(null)}
          onFertig={() => {
            aktualisieren();
            setSperren(null);
          }}
        />
      )}
    </>
  );
}

function SperrDialog({
  konto,
  onClose,
  onFertig,
}: {
  konto: User;
  onClose: () => void;
  onFertig: () => void;
}) {
  const [grund, setGrund] = useState('');

  const sperren = useMutation({
    mutationFn: () => sperreKonto(konto.id, grund.trim()),
    onSuccess: () => {
      toast.success(`${konto.email} gesperrt.`);
      onFertig();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-slate-900/40 p-0 sm:items-center sm:p-4">
      <div className="w-full max-w-lg rounded-t-xl bg-white p-5 shadow-xl sm:rounded-xl">
        <div className="flex items-start justify-between gap-3">
          <h2 className="text-base font-semibold text-slate-900">
            {konto.email} sperren
          </h2>
          <button
            onClick={onClose}
            aria-label="Schließen"
            className="rounded-md p-1 text-slate-400 hover:bg-slate-100"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        <div className="mt-4">
          <label htmlFor="sperr-grund" className="mb-1 block text-xs font-medium text-slate-700">
            Nachricht an den Betroffenen
          </label>
          <textarea
            id="sperr-grund"
            rows={3}
            value={grund}
            onChange={(e) => setGrund(e.target.value)}
            placeholder="z. B. Offene Rechnung seit drei Monaten. Bitte melden Sie sich unter …"
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
          />
          <p className="mt-1 text-xs text-slate-400">
            Steht beim nächsten Anmeldeversuch auf dem Bildschirm. Schreiben Sie
            so, wie Sie es dem Betroffenen sagen würden – und nennen Sie einen
            Weg, wie er die Sache klären kann.
          </p>
        </div>

        <p className="mt-4 rounded-md bg-slate-50 p-3 text-xs leading-relaxed text-slate-500">
          Die Daten des Kontos bleiben unberührt. Stammbäume, die ihm gehören,
          bleiben für ihre übrigen Mitglieder nutzbar.
        </p>

        <div className="mt-5 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="rounded-md border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Abbrechen
          </button>
          <button
            onClick={() => sperren.mutate()}
            disabled={sperren.isPending || grund.trim().length < 5}
            className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
          >
            {sperren.isPending ? 'Wird gesperrt…' : 'Konto sperren'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Seite ──────────────────────────────────────────────────────────────────

export function SperrenPage() {
  return (
    <div className="mx-auto max-w-4xl space-y-6 p-4 sm:p-6">
      <header>
        <h1 className="flex items-center gap-2 text-xl font-semibold text-slate-900 sm:text-2xl">
          <ShieldOff className="h-5 w-5 text-slate-400" aria-hidden="true" />
          Sperren
        </h1>
        <p className="mt-0.5 text-sm text-slate-500">
          Wer gerade nicht hereinkommt – und warum.
        </p>
      </header>

      <Anmeldesperren />
      <Kontosperren />
    </div>
  );
}

export default SperrenPage;
